import { createSelector } from "@reduxjs/toolkit";
import type {
  IAutoPipeRoutingInput,
  IAutoPipeRoutingRoom,
  IDevice,
  IPipeElevationsM,
} from "@multiconsult-group/multiplacer-core";
import type { IPixCircle } from "@multiconsult-group/multiplacer-ui";
import type { IRootState } from "../../../app/store";
import {
  AUTO_PIPE_ROUTING_SYSTEM_TYPE_TO_DEVICE_CATEGORY,
  selectAutoPipeRoutingConnectionPoints,
  selectAutoPipeRoutingCostSettings,
  selectAutoPipeRoutingPipeElevationsMmAboveFloorBySystem,
  selectSelectedAutoPipeRoutingSystemType,
} from "../../pipeRoutingSlice";
import { selectProjectNo } from "../../designSlice";
import { selectDevicesInSelectedRooms } from "../../devices/deviceSelectors";
import { getWorldZMFromFloorAndCeilingHeight } from "../../devices/deviceUtils";
import { selectHttpMcbimSpacesResult } from "../../../McBim/httpMcBimSelectors";
import { selectedRooms } from "../../../Room/roomSelectors";

const EMPTY_AUTO_PIPE_ROUTING_DEVICES: IDevice[] = [];
const EMPTY_AUTO_PIPE_ROUTING_ROOMS: IAutoPipeRoutingRoom[] = [];
const selectCurrentFloorWorldElevationM = (state: IRootState) =>
  state.design.floorAndJob?.elevation;
const selectCurrentFloorName = (state: IRootState) => state.design.floorAndJob?.floorName;

export const selectDevicesForSelectedAutoPipeRoutingSystemType = createSelector(
  [selectDevicesInSelectedRooms, selectSelectedAutoPipeRoutingSystemType],
  (devicesInSelectedRooms, selectedAutoPipeRoutingSystemType): IDevice[] => {
    if (!selectedAutoPipeRoutingSystemType) {
      return EMPTY_AUTO_PIPE_ROUTING_DEVICES;
    }

    const deviceCategory =
      AUTO_PIPE_ROUTING_SYSTEM_TYPE_TO_DEVICE_CATEGORY[selectedAutoPipeRoutingSystemType];

    return devicesInSelectedRooms.filter((device) => device.deviceCategory === deviceCategory);
  }
);

export const selectPipeElevationsMForSelectedAutoPipeRoutingSystemType = createSelector(
  [
    selectSelectedAutoPipeRoutingSystemType,
    selectAutoPipeRoutingPipeElevationsMmAboveFloorBySystem,
    selectCurrentFloorWorldElevationM,
  ],
  (
    selectedAutoPipeRoutingSystemType,
    pipeElevationsMmAboveFloorBySystem,
    currentFloorWorldElevationM
  ): IPipeElevationsM | null => {
    if (!selectedAutoPipeRoutingSystemType) {
      return null;
    }

    const pipeElevationsMmAboveFloor =
      pipeElevationsMmAboveFloorBySystem[selectedAutoPipeRoutingSystemType];

    if (!pipeElevationsMmAboveFloor) {
      return null;
    }

    return {
      xDirection: getWorldZMFromFloorAndCeilingHeight(
        currentFloorWorldElevationM,
        pipeElevationsMmAboveFloor.xDirectionMmAboveFloor
      ),
      yDirection: getWorldZMFromFloorAndCeilingHeight(
        currentFloorWorldElevationM,
        pipeElevationsMmAboveFloor.yDirectionMmAboveFloor
      ),
    };
  }
);

export const selectAutoPipeRoutingRooms = createSelector(
  [selectedRooms, selectHttpMcbimSpacesResult],
  (selectedRoomNumbers, mcbimSpaces): IAutoPipeRoutingRoom[] => {
    if (selectedRoomNumbers.length === 0) {
      return EMPTY_AUTO_PIPE_ROUTING_ROOMS;
    }

    const selectedRoomNumberSet = new Set(selectedRoomNumbers);

    return mcbimSpaces.flatMap((space) => {
      if (!selectedRoomNumberSet.has(space.name) || !space.polygon || space.polygon.length < 4) {
        return [];
      }

      return [
        {
          roomNumber: space.name,
          roomName: space.longName ?? undefined,
          polygon: space.polygon as IAutoPipeRoutingRoom["polygon"],
        },
      ];
    });
  }
);

export const selectAutoPipeRoutingConnectionPointCircles = createSelector(
  [selectAutoPipeRoutingConnectionPoints],
  (connectionPoints): IPixCircle[] =>
    connectionPoints.map((connectionPoint, index) => ({
      id: `auto-pipe-routing-connection-point-${index + 1}`,
      position: [connectionPoint[0], connectionPoint[1]],
      radius: 1.2,
      color: "#ffd43b",
      opacity: 0.9,
      isClickable: false,
      text: {
        show: true,
        values: [`Anslutning ${index + 1}`],
        color: "#1c1c1c",
        fontSize: "1",
      },
    }))
);

export const selectAutoPipeRoutingInput = createSelector(
  [
    selectProjectNo,
    selectCurrentFloorName,
    selectAutoPipeRoutingRooms,
    selectDevicesForSelectedAutoPipeRoutingSystemType,
    selectPipeElevationsMForSelectedAutoPipeRoutingSystemType,
    selectAutoPipeRoutingConnectionPoints,
    selectAutoPipeRoutingCostSettings,
  ],
  (
    projectNo,
    floorName,
    selectedRooms,
    devicesToConnect,
    pipeElevationsM,
    connectionPoints,
    costSettings
  ): IAutoPipeRoutingInput => {
    if (!projectNo) {
      throw new Error("selectAutoPipeRoutingInput: projectNo is missing");
    }

    if (!floorName) {
      throw new Error("selectAutoPipeRoutingInput: floorName is missing");
    }

    if (!pipeElevationsM) {
      throw new Error(
        "selectAutoPipeRoutingInput: pipe elevations are missing for selected system"
      );
    }

    if (connectionPoints.length === 0) {
      throw new Error("selectAutoPipeRoutingInput: connection points are missing");
    }

    console.info("selectAutoPipeRoutingInput: cost settings from GUI", {
      roomCrossingPenaltyM: costSettings.roomCrossingPenaltyM,
      corridorRoomFactor: costSettings.corridorRoomFactor,
      blockedRoomFactor: costSettings.blockedRoomFactor,
      verticalPenaltyFactor: costSettings.verticalPenaltyFactor,
      perimeterPenaltyFactor: costSettings.perimeterPenaltyFactor,
    });

    return {
      projectNo,
      floorName,
      selectedRooms,
      devicesToConnect,
      pipeElevationsM,
      connectionPoints,
      roomCrossingPenaltyM: costSettings.roomCrossingPenaltyM,
      corridorRoomFactor: costSettings.corridorRoomFactor,
      blockedRoomFactor: costSettings.blockedRoomFactor,
      verticalPenaltyFactor: costSettings.verticalPenaltyFactor,
      perimeterPenaltyFactor: costSettings.perimeterPenaltyFactor,
    };
  }
);
